import { Search, SelectionIndicator } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ItemCard from '../ItemCard/ItemCard';
import * as Ui from './SelectItemsModal.styles';

const SelectItemsModal = ({
  isOpen,
  onClose,
  onSelect,
  initialSelected,
  items,
  isLoading = false,
  getItemKey,
  isMultiple = true,
  getItemCardProps,
  onSearch,
}) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { t: tApp } = useTranslation('app');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(initialSelected);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(initialSelected);
      setSearch('');
      setShowSelectedOnly(false);
      onSearch('');
    }
  }, [initialSelected, isOpen, onSearch]);

  const selectedKeys = useMemo(
    () => new Set(selected.map((item) => getItemKey(item))),
    [getItemKey, selected],
  );

  const visibleItems = useMemo(() => {
    if (showSelectedOnly) {
      return items.filter((item) => selectedKeys.has(getItemKey(item)));
    }

    return items;
  }, [getItemKey, items, selectedKeys, showSelectedOnly]);

  const handleToggle = (item) => {
    const key = getItemKey(item);

    if (isMultiple) {
      setSelected((prev) =>
        selectedKeys.has(key)
          ? prev.filter((selectedItem) => getItemKey(selectedItem) !== key)
          : [...prev, item],
      );
      return;
    }

    setSelected(selectedKeys.has(key) ? [] : [item]);
  };

  const handleSelect = () => {
    onSelect(selected);
    onClose();
  };

  const handleClose = () => {
    setSelected(initialSelected);
    setShowSelectedOnly(false);
    setSearch('');
    onSearch('');
    onClose();
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    onSearch(value);
  };

  const confirmLabel =
    selected.length > 0 ? `${tApp('confirm')} (${selected.length})` : tApp('confirm');

  return (
    <Ui.StyledModal
      open={isOpen}
      header={{ title: t(isMultiple ? 'selectItems' : 'selectItem') }}
      closeOnDocumentClick
      onClose={handleClose}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        paddingBottom: 0,
      }}
      footer={() => (
        <Ui.Footer>
          <Button variant="white" onClick={handleClose} className="rounded">
            {tApp('cancel')}
          </Button>
          <Button
            variant="primary"
            className="rounded"
            disabled={selected.length === 0}
            onClick={handleSelect}
          >
            {confirmLabel}
          </Button>
        </Ui.Footer>
      )}
    >
      <Ui.ModalBody>
        <Ui.Toolbar>
          <Ui.SearchWrapper>
            <Search
              value={search}
              onChange={handleSearchChange}
              placeholder={t('searchPlaceholder')}
            />
          </Ui.SearchWrapper>
          <Ui.FilterButton
            type="button"
            $active={showSelectedOnly}
            onClick={() => setShowSelectedOnly((value) => !value)}
          >
            {t('selectedCount', { count: selected.length })}
          </Ui.FilterButton>
        </Ui.Toolbar>
        <Ui.ScrollableWrapper>
          <Ui.ItemsGrid>
            {isLoading ? (
              <Ui.EmptyState>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </Ui.EmptyState>
            ) : visibleItems.length === 0 ? (
              <Ui.EmptyState>{t('noItemsFound')}</Ui.EmptyState>
            ) : (
              visibleItems.map((item) => {
                const isSelected = selectedKeys.has(getItemKey(item));

                return (
                  <Ui.Card
                    key={getItemKey(item)}
                    $selected={isSelected}
                    onClick={() => handleToggle(item)}
                  >
                    <ItemCard {...getItemCardProps(item, { t })} />
                    <SelectionIndicator selected={isSelected} />
                  </Ui.Card>
                );
              })
            )}
          </Ui.ItemsGrid>
        </Ui.ScrollableWrapper>
      </Ui.ModalBody>
    </Ui.StyledModal>
  );
};

SelectItemsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  initialSelected: PropTypes.arrayOf(PropTypes.object).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  getItemKey: PropTypes.func.isRequired,
  isMultiple: PropTypes.bool,
  getItemCardProps: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};

export default SelectItemsModal;
